import React, { useState } from 'react';
import { Empty, Typography } from 'antd';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';

import { useQuery } from '@tanstack/react-query';

import { TRADEBOOK } from '../../../../utilities/functions';

import SetCommentDrawer from './SetCommentDrawer';
import SetOverviewRow from './SetOverviewRow';

const { Title, Text } = Typography;

const SET_TYPE_LABELS = {
    strategy: 'Strategy Sets',
    noStrategy: 'No Strategy Sets'
};

const getSetTypeLabel = (setType) => SET_TYPE_LABELS[setType] || 'Sets';

// eslint-disable-next-line no-unused-vars -- Reason: legacy suppression retained until the owning code is migrated or cleaned up.
export default function SetOverviewPanel({ setType, sets, planId, onRefresh }) {
    const navigate = useNavigate();
    const [selectedSet, setSelectedSet] = useState(null);
    const [commentDrawerVisible, setCommentDrawerVisible] = useState(false);

    // Fetch all comments for this plan
    const { data: commentsData } = useQuery(
        ['all-set-comments', planId],
        () => TRADEBOOK.fetchAllSetComments(planId),
        {
            enabled: !!planId,
            staleTime: 30000
        }
    );

    // Handle clicking on a set to navigate to filtered trades
    // planId is passed from parent (always required for strategy sets)
    const handleSetClick = (set) => {
        // Navigate to trades page with filter params
        const filterParams = new URLSearchParams({
            setType,
            planId,
            setNumber: set.setNumber
        });
        navigate(`/trades?${filterParams.toString()}`);
    };

    // Handle clicking on comment indicator or opening comment drawer
    const handleCommentClick = (set) => {
        setSelectedSet(set);
        setCommentDrawerVisible(true);
    };

    const handleCloseDrawer = () => {
        setCommentDrawerVisible(false);
        setSelectedSet(null);
    };

    // Check if a set has a comment
    const hasComment = (setNumber) => {
        const comment = commentsData?.comments?.[setType]?.[setNumber]?.comment;
        return comment && comment.trim().length > 0;
    };

    if (!sets || sets.length === 0) {
        return (
            <div className="set-overview-panel empty">
                <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description={
                        <div>
                            <Title level={5}>No {getSetTypeLabel(setType)} Found</Title>
                            <Text type="secondary">
                                Assign trades to {getSetTypeLabel(setType).toLowerCase()} in the
                                Trade Evaluation section to see them here.
                            </Text>
                        </div>
                    }
                />
            </div>
        );
    }

    return (
        <div className="set-overview-panel">
            <div className="panel-header">
                <Title level={5}>{getSetTypeLabel(setType)}</Title>
                <Text type="secondary">{sets.length} sets</Text>
            </div>

            {/* Column Headers */}
            <div className="set-overview-headers">
                <div className="header-cell set-name">Set Name</div>
                <div className="header-cell trades">Trades</div>
                <div className="header-cell discipline">Discipline %</div>
                <div className="header-cell win-rate">Win Rate</div>
                <div className="header-cell avg-win">Avg Win</div>
                <div className="header-cell avg-loss">Avg Loss</div>
                <div className="header-cell ratio">W:L Ratio</div>
                <div className="header-cell avg-day">Trades/Day</div>
                <div className="header-cell pnl">Total PnL</div>
                <div className="header-cell outcomes">Outcomes</div>
            </div>

            {/* Set Rows */}
            <div className="set-overview-list">
                {sets.map((set) => (
                    <SetOverviewRow
                        key={`${planId}-${set.setNumber}`}
                        set={set}
                        setType={setType}
                        onClick={() => handleSetClick(set)}
                        hasComment={hasComment(set.setNumber)}
                        onCommentClick={() => handleCommentClick(set)}
                    />
                ))}
            </div>

            {/* Comment Drawer */}
            {selectedSet && (
                <SetCommentDrawer
                    visible={commentDrawerVisible}
                    onClose={handleCloseDrawer}
                    planId={planId}
                    setType={setType}
                    setNumber={selectedSet.setNumber}
                    setName={selectedSet.setName}
                />
            )}
        </div>
    );
}

SetOverviewPanel.propTypes = {
    setType: PropTypes.string.isRequired,
    planId: PropTypes.string.isRequired,
    sets: PropTypes.arrayOf(
        PropTypes.shape({
            setNumber: PropTypes.number.isRequired,
            setName: PropTypes.string.isRequired,
            totalTrades: PropTypes.number.isRequired,
            totalPnL: PropTypes.number.isRequired,
            winRate: PropTypes.number,
            averageWin: PropTypes.number,
            averageLoss: PropTypes.number,
            winLossRatio: PropTypes.number,
            disciplinePercent: PropTypes.number,
            avgTradesPerDay: PropTypes.number,
            tradeOutcomes: PropTypes.arrayOf(
                PropTypes.shape({
                    tradeId: PropTypes.string,
                    pnl: PropTypes.number,
                    isWin: PropTypes.bool
                })
            ),
            isFull: PropTypes.bool
        })
    ).isRequired,
    onRefresh: PropTypes.func.isRequired
};
